import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import {
  ProductTypeSerializer,
  ProductVariantSerializer,
} from "@app-contexts/public/features/products/data/types/products";
import { enums } from "@shared/constants";
import { X_SP_UUID } from "@shared/env";
import useAnalytics from "@shared/lib/analytics/hooks/useAnalytics";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { trackItemsInCart } from "@shared/modules/events/utils/detail-page-helpers";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { getExceptionToastDescription } from "@shared/utils/errors/error-utils";
import ReadMore from "@ui-kit/typography/ReadMore/ReadMore.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import useDebounce from "@shared/hooks/useDebounce";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";

import ItemQuantitySelectInfo from "@ui-application/data-display/ItemQuantitySelectInfo/ItemQuantitySelectInfo.web";
import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";

const notifier = NotifierFactory.createNotifier();

export interface ProductTypesContainerProps {
  product: ProductTypeSerializer;
  hasAllInPricing: boolean;
  isLoading: boolean;
  event: PublicBaseEventSerializer;
}
const defaultImage = `images/default-showpass-square.png`;

const ProductTypesContainer: React.FC<ProductTypesContainerProps> = ({
  product,
  hasAllInPricing,
  isLoading,
  event,
}) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const link = router?.query?.link?.toString();
  const analytics = useAnalytics();
  const { itemGroups, isExpired, modifyItems } = usePublicBasket();

  // initialize quantitySelected to 0 on the product_attributes object
  const [formattedProductVariants, setFormattedProductVariants] = useState<
    ProductVariantSerializer[]
  >(
    product?.product_attributes?.map((variant: ProductVariantSerializer) => {
      return {
        ...variant,
        quantitySelected:
          itemGroups?.filter(
            (ig: TicketItemGroupType) => ig.product_attribute === variant.id
          ).length || 0,
      };
    }) ?? []
  );

  // the currently-selected product variant on the variant dropdown
  // initialize to the first product variant
  const [selectedVariantInfo, setSelectedVariantInfo] =
    useState<ProductVariantSerializer>(formattedProductVariants[0]);

  const variantsInBasket = itemGroups.filter(
    (ig: TicketItemGroupType) =>
      ig.product_attribute === selectedVariantInfo?.id
  );

  const totalVariantsQuantity =
    variantsInBasket?.reduce((prev, curr) => prev + (curr?.quantity || 0), 0) ||
    0;

  // This controls the quantity amount requested to the API, changed by this component only.
  const [amountRequested, setAmountRequested] = useState({
    amount: totalVariantsQuantity,
  });
  const [debouncedAmountRequested] = useDebounce(amountRequested, 500);

  // This controls the quantity amount displayed to the user, changed by this component and by the basket.
  const [amountSelectedDisplay, setAmountSelectedDisplay] = useState(
    totalVariantsQuantity
  );

  const componentDidMount = useRef(false);
  const allowRequest = useRef(true);

  useEffect(() => {
    // Change the displayed quantity when the basket quantity changes.
    setAmountSelectedDisplay(totalVariantsQuantity);
  }, [totalVariantsQuantity]);

  useEffect(() => {
    // Change the displayed quantity when this component requests a change.
    if (allowRequest.current) {
      setAmountSelectedDisplay(amountRequested.amount ?? 0);
    }
  }, [amountRequested]);

  useEffect(() => {
    if (!componentDidMount.current) {
      // do nothing on the first component render
      componentDidMount.current = true;
      return;
    }

    // prevent this api call from firing automatically when the cart expires and item amount is reset to 0
    if (allowRequest.current) {
      modifyItems(
        {
          item: {
            id: selectedVariantInfo?.id,
            name: selectedVariantInfo?.name,
            inventory: selectedVariantInfo?.inventory,
            sku: selectedVariantInfo?.sku,
          },
          quantity: debouncedAmountRequested.amount,
        },
        {
          hold_slug: link,
        }
      )
        .then((returnedBasket) => {
          if (!returnedBasket) {
            return;
          }

          try {
            const spAddToCartCall = () => {
              logAppInfo("spAddToCartCall");
              UserInteractionLogger.addToCart({
                item: {
                  item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id,
                  type: selectedVariantInfo?.id,
                },
                count: debouncedAmountRequested.amount,
                venue: event?.venue?.name,
                basket: returnedBasket,
                product,
                productVariant: selectedVariantInfo,
              });
            };

            if (event.venue) {
              logAppInfo(`calling trackItemsInCart ${returnedBasket?.id}`);
              trackItemsInCart(
                debouncedAmountRequested.amount,
                returnedBasket,
                {
                  itemId: selectedVariantInfo?.id,
                  itemName: product.name,
                  itemVariant: selectedVariantInfo?.name,
                  itemType: enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id,
                },
                event.venue,
                analytics,
                spAddToCartCall,
                {
                  uuid: returnedBasket[X_SP_UUID],
                }
              );
            }
          } catch (e) {
            logAppInfo(
              `Returned basket truthiness: ${Boolean(returnedBasket)}`
            );
            recordAppError(e);
          }
        })
        .catch((err) => {
          // On error, revert displayed amount to basket amount
          setAmountSelectedDisplay(totalVariantsQuantity);
          allowRequest.current = false;
          setAmountRequested({ amount: totalVariantsQuantity });

          console.error(err); //eslint-disable-line

          notifier.error(
            getExceptionToastDescription(err) ?? t("Unknown error")
          );
          logAppInfo("Error changing product quantity");
          recordAppError(err);
        });
    } else {
      allowRequest.current = true;
    }
  }, [debouncedAmountRequested]); //eslint-disable-line

  // set amount on ticket card to 0 when cart expires
  // re-initialize cart
  useEffect(() => {
    if (isExpired) {
      allowRequest.current = false;
      setAmountRequested({ amount: 0 });
    }
  }, [isExpired]);

  const productInfo =
    product &&
    product.product_attributes &&
    product?.product_attributes?.length > 0
      ? {
          productVariants: formattedProductVariants,
          setProductVariants: setFormattedProductVariants,
          selectedProductVariant: selectedVariantInfo,
          setSelectedProductVariant: setSelectedVariantInfo,
        }
      : null;

  const handleAmountSelected = (amount: number) => {
    setAmountRequested({ amount });
  };

  return (
    <DetailCardContainer>
      {productInfo && product && product.currency && (
        <ItemQuantitySelectInfo
          productVariantData={productInfo}
          itemTitle={product.name}
          itemPrice={selectedVariantInfo?.price}
          itemImage={product.image ?? defaultImage}
          useServerlessResizer={!!product.image}
          currency={product.currency}
          feesPricingInfo={selectedVariantInfo?.fees_pricing_info}
          hasAllInPricing={hasAllInPricing}
          showQuantityButtons={!!selectedVariantInfo?.price}
          soldOut={false}
          maxSelectionAmount={selectedVariantInfo?.purchase_limit}
          amountSelected={amountSelectedDisplay}
          onAmountSelectedChange={handleAmountSelected}
          isLoading={isLoading}
        />
      )}
      <ReadMore
        descriptionHtml={product.description ?? ""}
        openReadMoreText={t("Read More")}
        closeReadMoreText={t("Show Less")}
      />
    </DetailCardContainer>
  );
};

export default React.memo(ProductTypesContainer);
